import classNames from "classnames";
import { capitalize } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { CheckboxInput } from "@fidget/common/components/form/checkboxInput";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import commonStyles from "../../../../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../../../../models";
import settingsStyles from "../../../../../../styles/settings.css";
import { SettingsForm } from "../../../../settingsForm";
import { WorkingHoursForm } from "../../../../settingsForms/workingHours/workingHoursForm";
import { SubtitleDescription } from "../../../../subtitleDescription";
import { ExistingTagGroupFormType } from "../../types";
import { useTagGroupsSettings } from "../../useTagGroupsSettings";
import {
  handleMessageWidgetsUpdate,
  mapExistingFormToUpdateForm,
  mapTagGroupToForm,
  validateTagGroupNoNameConflict,
  validateTagGroupNoTagsConflict
} from "../../utils";
import { GroupNameInput } from "../groupNameInput/groupNameInput";
import { TagsInput } from "../tagsInput/tagsInput";
import styles from "./groupEditScreen.css";
import { GroupActionsInput } from "../groupActions/groupActionsInput";
import { logger } from "@fidget/common/utils";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../../../../models/agentServices";

interface Props {
  group: ExistingTagGroupFormType;
  onGoBack(): void;
}

export const GroupEditScreen = observer(({ group, onGoBack }: Props) => {
  const { spinnerService } = useAgentServices();
  const { uiStore, messageWidgetsStore } = useStore();
  const { items, updateItem } = useTagGroupsSettings();
  const [name] = useState(group.$.name.$);
  const [wasEdited, setWasEdited] = useState(false);

  useEffect(() => {
    const setEdited = () => setWasEdited(true);

    group.$.name.onUpdate(setEdited);
    group.$.tagIds.onUpdate(setEdited);
    group.$.activeHours.onUpdate(setEdited);
    group.$.attachmentsEnabled.onUpdate(setEdited);
    group.$.messageWidgets.onUpdate(setEdited);
  }, [group]);

  const handleDiscard = () => {
    const item = items.find((i) => i.$.id.$ === group.$.id.$);
    if (item) {
      group.$.name.reset(item.$.name.$);
      group.$.activeHours.reset(item.$.activeHours.$);
      group.$.tagIds.reset(item.$.tagIds.$);
      group.$.attachmentsEnabled.reset(item.$.attachmentsEnabled.$);
      group.$.messageWidgets.reset(item.$.messageWidgets.$);

      setWasEdited(false);
    }
  };

  const handleSave = async () => {
    try {
      const validate = await group.validate();
      if (validate.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.wrong"));
        return;
      }
      if (!validateTagGroupNoNameConflict(group, items)) {
        uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.nameNonUnique"));
        return;
      }
      if (!validateTagGroupNoTagsConflict(group, items)) {
        uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.tagsNonUnique"));
        return;
      }

      const item = await spinnerService.updateTagGroup(
        group.$.id.$,
        mapExistingFormToUpdateForm(group)
      );

      await handleMessageWidgetsUpdate(
        spinnerService,
        group.$.id.$,
        group.$.messageWidgets.$.toEnable,
        group.$.messageWidgets.$.toDisable
      );

      await messageWidgetsStore.loadMessageWidgets();

      const updatedItem = mapTagGroupToForm(item, []);
      updateItem(updatedItem);
      setWasEdited(false);
    } catch (e) {
      logger.error({ message: "Couldn't update tag group", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const handleGoBack = () => {
    if (wasEdited) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    onGoBack();
  };

  const renderTitle = () => (
    <div className={classNames(commonStyles.title, styles.title)} onClick={handleGoBack}>
      <i className={classNames("icon-arrow", settingsStyles.rotated)} />
      <L10n id="settings.tagGroups.edit.label" values={{ name }} />
    </div>
  );

  const renderGroupNameSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.tagGroups.edit.name.label")}
        description={capitalize(
          intl.formatMessage("settings.tagGroups.form.name.description", {
            limit: staticConfig.tagGroupNameMaxCharacters
          })
        )}
      />
      <div className={commonStyles.content}>
        <div className={styles["name-input-container"]}>
          <GroupNameInput field={group.$.name} />
        </div>
      </div>
    </div>
  );

  const renderGroupTagsSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.tagGroups.edit.tags.label")}
        description={intl.formatMessage("settings.tagGroups.edit.tags.description")}
      />
      <div className={commonStyles.content}>
        <TagsInput
          tagIds={group.$.tagIds}
          onDelete={(tagId) => group.$.tagIds.reset(group.$.tagIds.$.filter((t) => t !== tagId))}
        />
      </div>
    </div>
  );

  const renderAttachmentsSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.tagGroups.edit.attachments.label")}
        description={intl.formatMessage("settings.tagGroups.edit.attachments.description")}
      />
      <div className={commonStyles.content}>
        <CheckboxInput
          value={group.$.attachmentsEnabled.$}
          id="attachments_enabled"
          label={intl.formatMessage("settings.tagGroups.edit.attachments.checkbox")}
          onChange={(value) => group.$.attachmentsEnabled.reset(value)}
        />
      </div>
    </div>
  );

  const renderGroupActiveHoursSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.tagGroups.edit.hours.label")}
        description={intl.formatMessage("settings.tagGroups.edit.hours.description")}
      />
      <div className={commonStyles.content}>
        <WorkingHoursForm
          hours={group.$.activeHours.$}
          onChange={(idx, value) => {
            const hours = group.$.activeHours.$.map((day, i) => {
              return idx === i ? value : day;
            });
            group.$.activeHours.reset(hours);
          }}
        />
      </div>
    </div>
  );

  const renderGroupActions = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.tagGroups.edit.actions.label")}
        description={intl.formatMessage("settings.tagGroups.edit.actions.description")}
      />

      <div className={commonStyles.content}>
        <GroupActionsInput groupId={group.$.id.$} messageWidgets={group.$.messageWidgets} />
      </div>
    </div>
  );

  return (
    <SettingsForm disabled={!wasEdited} onDiscard={handleDiscard} onSubmit={handleSave}>
      {renderTitle()}
      {renderGroupNameSection()}
      {renderGroupTagsSection()}
      {renderAttachmentsSection()}
      {renderGroupActions()}
      {renderGroupActiveHoursSection()}
    </SettingsForm>
  );
});
